import {
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
  IconButton,
  Avatar,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
} from "@mui/material";
import config from "../../utils/config";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { toast } from "react-toastify";
import ClearIcon from "@mui/icons-material/Clear";
import ChangePasswordDialog from "../ChangePasswordDialog/ChangePasswordDialog";
import { useState, useEffect } from "react";
import axios from "axios";
import { useDebouncedCallback } from "use-debounce";
import { useParams } from "react-router-dom";
import { validateEmail } from "../../utils/email-validator";

const MAX_ACCESS_LEVEL = 5;

function AddUser(props) {
  const { editUserId } = useParams();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [accessLevel, setAccessLevel] = useState("");
  const [file, setFile] = useState(null);
  const [imageInputValue, setImageInputValue] = useState("");
  const [initialPicture, setInitialPicture] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [organization, setOrganization] = useState([]);

  useEffect(() => {
    if (editUserId) {
      props.setTitle("Edit User");
    } else {
      props.setTitle("Add User");
    }
  }, [editUserId]); //eslint-disable-line

  useEffect(() => {
    if (editUserId) {
      fetchUserInfo();
    } else {
      setName("");
      setEmail("");
      setAccessLevel("");
      setFile(null);
      setInitialPicture(null);
      setChangingPassword(false);
      setOrganization([]);
    }
  }, [editUserId]); //eslint-disable-line

  const fetchUserInfo = () => {
    axios
      .get(`${config.API_ENDPOINT}/users/${props.organization}/${editUserId}`, {
        headers: { "x-access-token": localStorage.getItem("access-token") },
      })
      .then((res) => {
        setName(res.data.name);
        setEmail(res.data.email);
        setAccessLevel(parseInt(res.data.accessLevel));
        setInitialPicture(
          res.data.avatarURL ? `${config.IMG_ENDPOINT}/uploads/${res.data.avatarURL}` : null
        );
        setOrganization(res.data.organization);
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const submit = useDebouncedCallback(
    () => {
      if (
        name === "" ||
        email === "" ||
        accessLevel === "" ||
        (organization.length === 0 && accessLevel !== 0)
      ) {
        toast.warning("You must fill all the required fields");
        return;
      }

      if (!validateEmail(email)) {
        toast.warning("Invalid email");
        return;
      }

      let data = new FormData();
      data.append("name", name);
      data.append("email", email);
      data.append("accessLevel", accessLevel);
      if (accessLevel !== 0) {
        data.append("organization", JSON.stringify(organization));
      }

      if (file != null) {
        data.append("image", file);
      }

      let q = `${config.API_ENDPOINT}/users/create`;
      if (editUserId != null) {
        q = `${config.API_ENDPOINT}/users/${props.organization}/${editUserId}`;

        if (initialPicture == null && file == null) {
          data.append("deleteAvatar", true);
        }
      }

      axios
        .post(q, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-access-token": localStorage.getItem("access-token"),
          },
        })
        .then(() => {
          if (!editUserId) {
            setName("");
            setEmail("");
            setAccessLevel("");
            setFile(null);
            setOrganization([]);
          }
          toast.success("Success!");
        })
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              localStorage.removeItem("access-token");
              window.location.replace("/login");
              break;
            case 409:
              toast.warning("This email is in use");
              break;
            case 413:
              toast.warning("The image you sent is too big.");
              break;
            case 404:
              toast.warning("You cannot edit this user");
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true }
  );

  const resetPassword = useDebouncedCallback(
    () => {
      axios
        .get(`${config.API_ENDPOINT}/users/${props.organization}/${editUserId}/reset-password`, {
          headers: { "x-access-token": localStorage.getItem("access-token") },
        })
        .then(() => toast.success("Success! The user will receive an email soon."))
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              localStorage.removeItem("access-token");
              window.location.replace("/login");
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true }
  );

  const handleClearPhoto = () => {
    setFile(null);
    setInitialPicture(null);
    setImageInputValue("");
  };

  const accessLevelSelectorUI = () => {
    const levelLabels = {
      0: "Administrator",
      1: "Organization Admin",
      2: "Organization Manager",
      3: "Events & Students Manager",
      4: "Box Office Manager",
      5: "Ticket Officer",
    };

    return (
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Access Level*</InputLabel>
        <Select
          value={accessLevel}
          label="Access Level*"
          onChange={(e) => setAccessLevel(e.target.value)}
        >
          {Array.from({ length: MAX_ACCESS_LEVEL + 1 }, (_, i) => (
            <MenuItem key={i} value={i}>
              {i} — {levelLabels[i]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  if (props.user.accessLevel > 1 && (editUserId == null || editUserId !== props.user._id))
    return null;

  return (
    <div>
      <Box sx={{ mb: 4, pb: 3, borderBottom: "1px solid rgba(19, 33, 68, 0.1)" }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>{editUserId ? "Edit User" : "Add User"}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>Manage user information, avatar, and access levels</Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Basic Information</Typography>
              <TextField
                variant="outlined"
                required
                fullWidth
                label="Email"
                name="email"
                autoFocus
                type="email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                variant="outlined"
                required
                fullWidth
                label="Name"
                name="name"
                type="text"
                autoComplete="off"
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Profile Picture</Typography>
              <input
                accept="image/jpg, image/jpeg, image/png"
                id="icon-button-file"
                type="file"
                hidden
                value={imageInputValue}
                onChange={(e) => {
                  setFile(e.target.files[0]);
                  setImageInputValue(e.target.value);
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <label htmlFor="icon-button-file">
                  <Button component="span" startIcon={<PhotoCamera />} size="small">
                    Upload Photo
                  </Button>
                </label>
                {(file || initialPicture) && (
                  <IconButton size="small" onClick={handleClearPhoto}>
                    <ClearIcon />
                  </IconButton>
                )}
              </Box>
            </CardContent>
          </Card>

          {props.user._id !== editUserId && (
            <>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Organizations</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 2 }}>Select at least 1 organization</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {props.organizations.map((org) => (
                      <FormControlLabel
                        key={org._id}
                        control={
                          <Checkbox
                            color="primary"
                            checked={organization.includes(org._id)}
                            onChange={(e) => {
                              let r = [...organization];
                              if (e.target.checked) {
                                r.push(org._id);
                              } else {
                                r.splice(r.indexOf(org._id), 1);
                              }
                              setOrganization(r);
                            }}
                          />
                        }
                        label={org.name}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Access Level & Permissions</Typography>
                  {accessLevelSelectorUI()}
                  <Box sx={{ mt: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Access Levels:</Typography>
                    <Typography variant="caption" component="div" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
                      <strong>0</strong> — Administrator (Full access)<br />
                      <strong>1</strong> — Organization Admin (Full org access)<br />
                      <strong>2</strong> — Organization Manager (Stats & level 3+)<br />
                      <strong>3</strong> — Events & Students Manager<br />
                      <strong>4</strong> — Box Office Manager (Payments & students)<br />
                      <strong>5</strong> — Ticket Officer (Check-in & validate)
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Preview</Typography>
              <Avatar
                src={file != null ? URL.createObjectURL(file) : initialPicture}
                sx={{ height: 100, width: 100, mb: 2 }}
              />
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Name</Typography>
                <Typography variant="body2">{name || "—"}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Email</Typography>
                <Typography variant="body2">{email || "—"}</Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Access Level</Typography>
                <Typography variant="body2">{accessLevel || "—"}</Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={submit}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {editUserId ? "Update User" : "Create User"}
                </Button>
                {editUserId && props.user.accessLevel <= 1 && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={resetPassword}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Reset Password
                  </Button>
                )}
                {props.user._id === editUserId && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setChangingPassword(true)}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Change Password
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ChangePasswordDialog open={changingPassword} onClose={() => setChangingPassword(false)} />
    </div>
  );
}

export default AddUser;
